import { useQuery } from '@tanstack/react-query';
import { Activity, RefreshCw, Zap, TrendingUp, AlertTriangle } from 'lucide-react';
import { getPerfTimings, getPerfAggregates, getMarketProvidersHealth } from '../../services/perfService';
import type { TimingSample, PathAggregate, ProviderHealth } from '../../services/perfService';

// ── Helpers ───────────────────────────────────────────────────────────────────

function msColor(ms: number): string {
  if (ms < 200)  return 'text-green-400';
  if (ms < 1000) return 'text-amber-400';
  return 'text-red-400';
}

function formatTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString();
}

function shortPath(path: string): string {
  return path.replace('/api/v1/', '');
}

function CacheTag({ cache }: { cache: string }) {
  if (!cache) return null;
  const style =
    cache === 'HIT'   ? 'bg-green-900/40 text-green-400 border-green-800/50' :
    cache === 'MISS'  ? 'bg-amber-900/40 text-amber-400 border-amber-800/50' :
    cache === 'STALE' ? 'bg-blue-900/40 text-blue-400 border-blue-800/50' :
                        'bg-slate-800 text-slate-400 border-slate-700/50';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-mono border ${style}`}>
      {cache}
    </span>
  );
}

function StatCard({ label, value, sub, color = 'text-slate-100' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function ProviderCard({ name, p }: { name: string; p: ProviderHealth }) {
  const statusColor =
    p.status === 'ok'           ? 'text-green-400 border-green-800/40 bg-green-900/10' :
    p.status === 'market-closed'? 'text-slate-400 border-slate-700/40 bg-slate-800/30' :
    p.status === 'error'        ? 'text-red-400 border-red-800/40 bg-red-900/10' :
                                  'text-amber-400 border-amber-800/40 bg-amber-900/10';
  const fetchColor = p.fetch_ms != null ? msColor(p.fetch_ms) : 'text-slate-500';

  return (
    <div className={`border rounded-lg p-4 space-y-2 ${statusColor}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-200">{name}</p>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${statusColor}`}>
          {p.status}
        </span>
      </div>
      <p className="text-xs text-slate-500">{p.exchange}</p>
      {p.fetch_ms != null && (
        <p className={`text-lg font-bold ${fetchColor}`}>
          {p.fetch_ms}ms
          <span className="text-xs font-normal text-slate-500 ml-1">last fetch</span>
        </p>
      )}
      {p.detail && (
        <p className="text-xs text-red-400 truncate" title={p.detail}>{p.detail}</p>
      )}
      {p.last_call && (
        <p className="text-xs text-slate-600">
          {new Date(p.last_call).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function PerformanceDashboard() {
  const { data: samples = [], isFetching: fetchingSamples, refetch: refetchSamples } = useQuery<TimingSample[]>({
    queryKey: ['perf-timings'],
    queryFn: () => getPerfTimings(200),
    refetchInterval: 30_000,
    retry: 1,
  });

  const { data: aggregates = {}, isFetching: fetchingAgg, refetch: refetchAgg } = useQuery<Record<string, PathAggregate>>({
    queryKey: ['perf-aggregates'],
    queryFn: getPerfAggregates,
    refetchInterval: 30_000,
    retry: 1,
  });

  const { data: providers, isFetching: fetchingProviders, refetch: refetchProviders } = useQuery({
    queryKey: ['market-providers-perf'],
    queryFn: getMarketProvidersHealth,
    refetchInterval: 30_000,
    retry: 1,
  });

  const isFetching = fetchingSamples || fetchingAgg || fetchingProviders;
  function refetch() { refetchSamples(); refetchAgg(); refetchProviders(); }

  // Summary stats
  const totalRequests = samples.length;
  const slowRequests  = samples.filter(s => s.ms >= 1000).length;
  const avgMs = totalRequests ? Math.round(samples.reduce((s, r) => s + r.ms, 0) / totalRequests) : 0;
  const maxMs = totalRequests ? Math.max(...samples.map(s => s.ms)) : 0;
  const cacheHits  = samples.filter(s => s.cache === 'HIT').length;
  const cacheTotal = samples.filter(s => s.cache !== '').length;
  const hitRate = cacheTotal ? Math.round((cacheHits / cacheTotal) * 100) : null;

  const aggRows = Object.entries(aggregates);
  const aggMaxMs = aggRows.length ? Math.max(...aggRows.map(([, v]) => v.max_ms)) : 1;

  // Slow samples (≥500ms), newest first, capped at 50
  const slowSamples = samples.filter(s => s.ms >= 500).slice(0, 50);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-teal-400" />
          <h1 className="text-xl font-semibold text-slate-100">API Performance</h1>
          <span className="text-xs text-slate-500">last {totalRequests} tracked requests</span>
        </div>
        <button
          onClick={refetch}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-slate-300 bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Avg response" value={`${avgMs}ms`} sub="all tracked" color={msColor(avgMs)} />
        <StatCard label="Max response" value={`${maxMs}ms`} sub="worst case"  color={msColor(maxMs)} />
        <StatCard label="Slow (≥1s)"   value={slowRequests} sub={`of ${totalRequests}`}
          color={slowRequests > 0 ? 'text-amber-400' : 'text-green-400'} />
        <StatCard label="Cache hit rate" value={hitRate !== null ? `${hitRate}%` : '—'}
          sub={`${cacheHits}/${cacheTotal} cached`}
          color={hitRate !== null && hitRate >= 70 ? 'text-green-400' : 'text-amber-400'} />
        <StatCard label="Endpoints" value={aggRows.length} sub="unique paths" />
      </div>

      {/* Option C — Provider fetch timing */}
      {providers && (
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Market Data Provider Latency
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ProviderCard name="IRESS (JSE)" p={providers.iress} />
            <ProviderCard name="EODHD (Global)" p={providers.eodhd} />
          </div>
        </div>
      )}

      {/* Aggregates by endpoint */}
      <div>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Endpoint Breakdown
        </h2>
        {aggRows.length === 0 ? (
          <div className="bg-slate-800/30 border border-slate-700/40 rounded-lg p-6 text-center text-slate-500 text-sm">
            No data yet — make some requests to the Markets or Signals endpoints.
          </div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Endpoint</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Calls</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Avg</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">p50</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">p95</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Max</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Profile</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {aggRows.map(([path, agg]) => (
                  <tr key={path} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-300 max-w-xs truncate" title={path}>
                      {shortPath(path)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-400 tabular-nums">{agg.count}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${msColor(agg.avg_ms)}`}>
                      {agg.avg_ms}ms
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${msColor(agg.p50_ms)}`}>
                      {agg.p50_ms}ms
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${msColor(agg.p95_ms)}`}>
                      {agg.p95_ms}ms
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${msColor(agg.max_ms)}`}>
                      {agg.max_ms}ms
                    </td>
                    <td className="px-4 py-2.5 w-28">
                      <div className="w-full bg-slate-700/40 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${agg.avg_ms >= 1000 ? 'bg-red-500' : agg.avg_ms >= 300 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min((agg.avg_ms / aggMaxMs) * 100, 100).toFixed(1)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent slow requests */}
      <div>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Slow Requests ≥ 500ms
        </h2>
        {slowSamples.length === 0 ? (
          <div className="bg-slate-800/30 border border-slate-700/40 rounded-lg p-6 text-center text-green-400 text-sm">
            ✓ All tracked calls under 500ms
          </div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Time</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Endpoint</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">ms</th>
                  <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-center px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Cache</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {slowSamples.map((s, i) => (
                  <tr key={`${s.ts}-${s.path}-${i}`} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-2.5 text-slate-500 text-xs tabular-nums whitespace-nowrap">{formatTime(s.ts)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-300 max-w-xs truncate" title={s.path}>
                      <span className="text-slate-500 mr-1">{s.method}</span>
                      {shortPath(s.path)}
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${msColor(s.ms)}`}>
                      {s.ms}
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums text-xs ${s.status >= 400 ? 'text-red-400' : 'text-slate-400'}`}>
                      {s.status}
                    </td>
                    <td className="px-3 py-2.5 text-center"><CacheTag cache={s.cache} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 text-xs text-slate-500 border-t border-slate-700/50 pt-4">
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"/> &lt;200ms — fast</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"/> 200–999ms — acceptable</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"/> ≥1000ms — slow</div>
        <div className="flex items-center gap-1.5 ml-auto"><Zap className="w-3.5 h-3.5" /> auto-refreshes every 30s</div>
      </div>
    </div>
  );
}
